import { Appointment } from '../../../models/appointment/Appointment'
import { Assignment } from '../../../models/assignments/Assignments'
import { ClientObject } from '../../../models/client/Client'
import { Episode, EpisodeParticipantRole } from '../../../models/episodes/Episodes'
import { APPOINTMENT_STATUS } from '../../../models/session/Session'
import { getCaregiverContactFromIntakeForm, getGuardianInfoForEpisode } from '../../appointments/utils'
import { LTSessionTypes } from '../constants'

type DbtTeensAttendeeRole = 'teen' | 'caregiver'
type DbtTeensSessionNumberAppointment = Pick<Appointment, 'episodeId' | 'sessionNumber' | 'appointmentStatus'>

/**
 * Exact bookable subtype labels for scheduling and confirmation. These intentionally differ from
 * the rolled-up DBT Teens track labels used by Notes and Upcoming Sessions.
 */
export const DBT_TEENS_SCHEDULING_SESSION_TYPE_OPTIONS = [
  {
    value: LTSessionTypes.DBT_TEENS_INTAKE_SESSION,
    label: 'Intake session',
    attendeeRoles: ['teen', 'caregiver'],
  },
  {
    value: LTSessionTypes.DBT_TEENS_FOLLOWUP_SESSION,
    label: 'Follow-up session',
    attendeeRoles: ['teen'],
  },
  {
    value: LTSessionTypes.DBT_TEENS_FAMILY_SESSION,
    label: 'Family session',
    attendeeRoles: ['teen', 'caregiver'],
  },
  {
    value: LTSessionTypes.DBT_TEENS_MEDICATION_INITIAL_SESSION,
    label: 'Medication management intake session',
    attendeeRoles: ['teen', 'caregiver'],
  },
  {
    value: LTSessionTypes.DBT_TEENS_MEDICATION_FOLLOWUP_SESSION,
    label: 'Medication management follow-up session',
    attendeeRoles: ['teen', 'caregiver'],
  },
] as const

export const getDbtTeensSchedulingSessionTypeMetadata = (sessionType?: string) =>
  DBT_TEENS_SCHEDULING_SESSION_TYPE_OPTIONS.find(({ value }) => value === sessionType)

/**
 * Session number the next booking will take: one past the largest number already used in the
 * episode. Canceled appointments release their number — a canceled Session 1 has to leave Session 1
 * bookable, or the intake option (gated on this number) would be gone for good. Missed sessions
 * still consume theirs.
 */
export const getNextDbtTeensSessionNumber = ({
  appointments,
  episodeId,
}: {
  appointments: DbtTeensSessionNumberAppointment[]
  episodeId?: string | null
}) => {
  const episodeAppointments = appointments.filter(
    (appointment) =>
      appointment.appointmentStatus !== APPOINTMENT_STATUS.CANCELED &&
      (!episodeId || appointment.episodeId === episodeId),
  )

  return (
    episodeAppointments.reduce(
      (largestSessionNumber, appointment) => Math.max(largestSessionNumber, Number(appointment.sessionNumber) || 0),
      0,
    ) + 1
  )
}

export const getDbtTeensSchedulingAttendees = ({
  sessionType,
  teenName,
  caregiverName,
}: {
  sessionType?: string
  teenName: string
  caregiverName: string
}) => {
  const metadata = getDbtTeensSchedulingSessionTypeMetadata(sessionType)
  if (!metadata) {
    return []
  }

  const attendeeNames: Record<DbtTeensAttendeeRole, string> = {
    teen: teenName,
    caregiver: caregiverName,
  }
  return metadata.attendeeRoles.map((role) => ({ name: attendeeNames[role], role }))
}

export const getDbtTeensSchedulingSessionTypeOptions = ({
  teenName,
  caregiverName,
  sessionNumber,
}: {
  teenName: string
  caregiverName: string
  sessionNumber: number | undefined
}) =>
  DBT_TEENS_SCHEDULING_SESSION_TYPE_OPTIONS.filter(
    ({ value }) => sessionNumber === 1 || value !== LTSessionTypes.DBT_TEENS_INTAKE_SESSION,
  ).map(({ label, value }) => ({
    label,
    value,
    helperText: `Attendees – ${getDbtTeensSchedulingAttendees({ sessionType: value, teenName, caregiverName })
      .map(({ name, role }) => `${name} (${role})`)
      .join(', ')}`,
  }))

export const getDbtTeensSchedulingParticipantNames = ({
  client,
  episode,
  appointments = [],
  assignments,
}: {
  client?: ClientObject
  episode?: Episode
  appointments?: Appointment[]
  assignments?: Assignment[]
}) => {
  const episodeAppointments = episode?.id
    ? appointments.filter((appointment) => appointment.episodeId === episode.id)
    : appointments
  const guardianInfo = getGuardianInfoForEpisode(episodeAppointments, episode?.participants)
  const intakeCaregiver = getCaregiverContactFromIntakeForm(assignments, episode?.id)
  const activeSecondaryPatient = episode?.participants?.find(
    (participant) =>
      participant.role === EpisodeParticipantRole.SECONDARY_PATIENT && participant.status?.toLowerCase() === 'active',
  )
  const secondaryPatientAppointment = activeSecondaryPatient
    ? episodeAppointments.find(
        (appointment) => appointment.userInfo?.lyraId === activeSecondaryPatient.participant_lyra_id,
      )
    : undefined
  const caregiverFirstName =
    secondaryPatientAppointment?.userInfo?.firstName || guardianInfo.guardianFirstName || intakeCaregiver.firstName
  const caregiverLastName =
    secondaryPatientAppointment?.userInfo?.lastName || guardianInfo.guardianLastName || intakeCaregiver.lastName
  const appointmentCaregiverName = guardianInfo.guardianFirstName || guardianInfo.guardianLastName
  const intakeCaregiverName = intakeCaregiver.firstName || intakeCaregiver.lastName

  return {
    teenName: client?.preferred_first_name || client?.first_name || 'Teen',
    caregiverName:
      secondaryPatientAppointment?.userInfo?.firstName ||
      appointmentCaregiverName ||
      intakeCaregiverName ||
      'Caregiver',
    caregiverFirstName,
    caregiverLastName,
  }
}
